import { CheckCircle2, MailCheck, RefreshCw } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InlineSpinner } from "@/components/common/states";
import { authApi } from "@/lib/api/endpoints";
import { errorMessage } from "@/lib/api/errors";
import { useResendVerification } from "@/hooks/use-account";
import { useAuth } from "@/providers/auth-provider";
import { AuthLayout } from "./auth-layout";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { status, user, refreshTokenAndIdentity } = useAuth();
  const resend = useResendVerification();

  const linkToken = params.get("token");
  const knownEmail = params.get("email") ?? user?.email ?? "";

  const [email, setEmail] = React.useState(knownEmail);
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [state, setState] = React.useState<"idle" | "verifying" | "done">(
    linkToken ? "verifying" : "idle",
  );

  const finish = React.useCallback(
    async (refreshRequired: boolean) => {
      setState("done");
      // Verified status is baked into the access token — the app won't treat
      // them as verified until we refresh.
      if (refreshRequired && status !== "anonymous") {
        await refreshTokenAndIdentity().catch(() => undefined);
      }
    },
    [refreshTokenAndIdentity, status],
  );

  // A link lands here with ?token= — verify it once, on mount.
  const attempted = React.useRef(false);
  React.useEffect(() => {
    if (!linkToken || attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        const result = await authApi.verifyEmail({ token: linkToken });
        await finish(result.refreshRequired);
      } catch (e) {
        setState("idle");
        setError(errorMessage(e, "That link has expired or has already been used."));
      }
    })();
  }, [linkToken, finish]);

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    if (!email.trim()) {
      setError("Enter the email you signed up with.");
      return;
    }
    setState("verifying");
    try {
      const result = await authApi.verifyEmail({ email: email.trim(), code: code.trim() });
      await finish(result.refreshRequired);
    } catch (e) {
      setState("idle");
      setError(errorMessage(e, "That code didn't work."));
    }
  };

  if (state === "done") {
    return (
      <AuthLayout title="Email verified" subtitle="You're all set.">
        <Alert variant="success">
          <CheckCircle2 />
          <AlertTitle>Verification complete</AlertTitle>
          <AlertDescription>
            Checkout and email notifications are now unlocked for your account.
          </AlertDescription>
        </Alert>
        <Button
          className="mt-5 w-full"
          onClick={() => navigate(status === "anonymous" ? "/login" : "/dashboard", { replace: true })}
        >
          {status === "anonymous" ? "Sign in" : "Go to dashboard"}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Verify your email"
      subtitle={
        knownEmail ? (
          <>
            We sent a link and a 6-digit code to <span className="font-medium">{knownEmail}</span>.
          </>
        ) : (
          "We sent you a link and a 6-digit code."
        )
      }
      footer={
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      {state === "verifying" && linkToken ? (
        <div className="flex items-center gap-3 rounded-xl border p-4 text-sm text-muted-foreground">
          <InlineSpinner /> Verifying your link…
        </div>
      ) : (
        <form onSubmit={submitCode} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!knownEmail && (
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
              />
            </Field>
          )}

          <Field label="6-digit code" htmlFor="code" hint="Or just click the link in the email.">
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="text-center text-xl tracking-[0.5em] tabular"
              placeholder="······"
              autoFocus
            />
          </Field>

          <Button type="submit" size="lg" className="w-full" loading={state === "verifying"}>
            <MailCheck className="size-4" />
            Verify email
          </Button>

          {status === "authenticated" && (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              loading={resend.isPending}
              onClick={() =>
                resend.mutate(undefined, {
                  onSuccess: () => toast.success("Verification email sent"),
                  onError: (e) => toast.error(errorMessage(e)),
                })
              }
            >
              <RefreshCw className="size-4" />
              Resend the email
            </Button>
          )}
        </form>
      )}
    </AuthLayout>
  );
}
